/**
 * Comparison.jsx — main page
 *
 * Handles: KPIHeader, KPISubHeader, session restore, Redux sync, project title bar.
 * All content delegated to <ComparisonView pid={pid} viewIds={viewIds} />.
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch }    from 'react-redux';
import { Navigate }                    from 'react-router-dom';

import KPIHeader            from '../../Header/KPIHeader';
import KPISubHeader         from '../../Header/SubHeader/KPISubHeader';
import ComparsionDefinitions from '../../Defintions/ComparsionDefinitions';
import ComparisonView       from '../../KPI/Comparison/ComparisonView';

import { getSession }        from '../../utils/session';
import { setProjectDetails } from '../../userSlice';

const Comparison = () => {
  const dispatch = useDispatch();

  // ── Redux ──────────────────────────────────────────────────────────────────
  const reduxEmail       = useSelector((state) => state.user.email);
  const reduxProjectName = useSelector((state) => state.user.projectName);
  const reduxPid         = useSelector((state) => state.user.projectId);
  const reduxViewIds     = useSelector((state) => state.user.viewIds);
  const isSuperuser      = useSelector(s => s.user.isSuperuser);
  
  const [email,       setEmail]       = useState(reduxEmail       || '');
  const [projectName, setProjectName] = useState(reduxProjectName || '');
  const [pid,         setPid]         = useState(reduxPid         || '');
  const [viewIds,     setViewIds]     = useState(reduxViewIds     || []);
  const [loading,     setLoading]     = useState(true);

  // ── Session restore ────────────────────────────────────────────────────────
  useEffect(() => {
    // Redux already has data (normal navigation) — use it directly
    if (reduxEmail && reduxPid) {
      setEmail(reduxEmail);
      setPid(reduxPid);
      setProjectName(reduxProjectName || '');
      setViewIds(reduxViewIds || []);
      setLoading(false);
      return;
    }

    // Fall back to sessionStorage (hard refresh / direct URL)
    const session = getSession();
    if (session?.email) {
      setEmail(session.email);
      setPid(session.projectId       || '');
      setProjectName(session.projectName    || '');
      setViewIds(session.viewIds         || []);

      dispatch(setProjectDetails({
        email:            session.email,
        projectName:      session.projectName,
        projectId:        session.projectId,
        organizationName: session.organizationName,
        roleId:           session.roleId,
        viewIds:          session.viewIds || [],
      }));
    }
    setLoading(false);
  }, [dispatch, reduxEmail, reduxPid, reduxProjectName, reduxViewIds]);

  // Keep in sync if Redux changes (user switches project without hard refresh)
  useEffect(() => {
    if (reduxPid)             setPid(reduxPid);
    if (reduxViewIds?.length) setViewIds(reduxViewIds);
  }, [reduxPid, reduxViewIds]);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (loading) return null;
  if (!email)  return <Navigate to="/" replace />;

  return (
    <div>
      <div className="z-50">
        <KPIHeader />
        <KPISubHeader />
      </div>

      <div className="mt-32">

        {/* ── All content sections, view-gated inside ComparisonView ────────── */}
        <ComparisonView pid={pid} viewIds={viewIds} />
      </div>
    </div>
  );
};

export default Comparison;